// src/hooks/useTabParam.ts
//
// Tab selection that lives in the URL instead of component state.
//
// The admin portal is two levels of tabs deep (hamburger tab → inner sub-tab),
// and all of it used to be `useState`: a refresh, a bookmark, or a link shared
// with someone dumped you back on Dashboard. Holding the selection in the query
// string makes every tab addressable (`/admin?tab=orders&sub=shipped`),
// refresh-stable, and Back/Forward-navigable for free.
//
// Rules that matter:
//  - An unknown or missing value falls back to the default rather than
//    rendering nothing, so a hand-typed or stale URL degrades to the tab's
//    normal landing state.
//  - Selecting the default drops the param, keeping the common URL clean —
//    `/admin` and `/admin?tab=dashboard` are the same place.
//  - `clears` lets an outer tab wipe the inner one on change, so switching
//    hamburger tabs doesn't carry a foreign `sub` value across.

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

interface TabParamOptions {
  /** Other params to delete when this one CHANGES (e.g. a stale inner tab). */
  clears?: string[];
}

/**
 * Read/write a single tab-selection query param.
 *
 * @param key      Query-string key (e.g. "tab", "sub").
 * @param valid    Every value the caller knows how to render.
 * @param fallback Value used when the param is absent or unrecognized.
 */
export function useTabParam<T extends string>(
  key: string,
  valid: readonly T[],
  fallback: T,
  options: TabParamOptions = {},
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();
  const { clears } = options;

  const raw = params.get(key);
  const value =
    raw !== null && (valid as readonly string[]).includes(raw)
      ? (raw as T)
      : fallback;

  const setValue = useCallback(
    (next: T) => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          const changed = (updated.get(key) ?? fallback) !== next;
          if (next === fallback) updated.delete(key);
          else updated.set(key, next);
          if (changed) {
            for (const other of clears ?? []) {
              if (other !== key) updated.delete(other);
            }
          }
          return updated;
        },
        // Push, not replace: Back should walk you through the tabs you visited.
        { replace: false },
      );
    },
    [setParams, key, fallback, clears],
  );

  return [value, setValue];
}

export default useTabParam;
